import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { debounce } from 'lodash';

// Define the species type
export interface Species {
  id: string;
  speciesId: string;
  status: string;
  note: string | null;
  order: string;
  scientificName: string;
  englishName: string;
}

interface OrgSpeciesSearchProps {
  orgId: string;
  onSelect: (species: Species) => void;
  selectedSpecies?: Species;
  placeholder?: string;
  className?: string;
}

const OrgSpeciesSearch: React.FC<OrgSpeciesSearchProps> = ({
  orgId,
  onSelect,
  selectedSpecies,
  placeholder = 'Search for a species...',
  className
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Create a debounced search function
  const debouncedSearch = useRef(
    debounce((term: string) => {
      setSearchTerm(term);
    }, 300)
  ).current;

  // Fetch species based on search term
  const { data: species, isLoading } = useQuery({
    queryKey: ['species', searchTerm],
    queryFn: async () => {
      const url = searchTerm
        ? `/api/org/${orgId}/species?search=${encodeURIComponent(searchTerm)}`
        : `/api/org/${orgId}/species`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch species');
      }
      return response.json() as Promise<Species[]>;
    },
    enabled: isOpen || !!selectedSpecies,
  });

  // Handle outside clicks to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedSearch(value);
    setIsOpen(true);
  };

  // Handle species selection
  const handleSelectSpecies = (species: Species) => {
    onSelect(species);
    setIsOpen(false);
  };

  // Clear selection
  const handleClearSelection = () => {
    onSelect({} as Species);
    setSearchTerm('');
  };

  return (
    <div className={cn("relative w-full", className)} ref={dropdownRef}>
      <div className="relative">
        {selectedSpecies?.id ? (
          <div className="flex items-center justify-between border rounded-md p-2">
            <div>
              <div className="font-medium">{selectedSpecies.englishName}</div>
              <div className="text-sm text-gray-500 italic">{selectedSpecies.scientificName}</div>
            </div>
            <Badge variant={selectedSpecies.status === 'resident' ? 'default' : 'secondary'} className="ml-2">
              {selectedSpecies.status}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto"
              onClick={handleClearSelection}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={placeholder}
              className="pl-8"
              onChange={handleInputChange}
              onFocus={() => setIsOpen(true)}
            />
            {isLoading && (
              <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>
        )}
      </div>

      {isOpen && !selectedSpecies?.id && (
        <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mr-2" />
              <span>Loading species...</span>
            </div>
          ) : species && species.length > 0 ? (
            <ul>
              {species.map((species) => (
                <li
                  key={species.id}
                  className="px-3 py-2 hover:bg-muted cursor-pointer flex items-center justify-between"
                  onClick={() => handleSelectSpecies(species)}
                >
                  <div>
                    <div className="font-medium">{species.englishName}</div>
                    <div className="text-sm text-gray-500 italic">{species.scientificName}</div>
                  </div>
                  <Badge variant={species.status === 'resident' ? 'default' : 'secondary'} className="ml-2">
                    {species.status}
                  </Badge>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-3 text-center text-muted-foreground">
              {searchTerm ? 'No species found' : 'Start typing to search for species'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrgSpeciesSearch;
